import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The Alpha chat window's size and position.
 *
 * SIZE. The window resizes from its top edge (height), its left edge (width)
 * and its top-left corner (both) — drag them, or focus an edge and use the
 * arrow keys (Home / End go to the limits). Width stays between
 * CHAT_MIN_WIDTH and CHAT_MAX_WIDTH, height between CHAT_MIN_HEIGHT and
 * CHAT_MAX_HEIGHT, and neither ever grows past the screen. The bottom-right
 * corner stays where it is while resizing.
 *
 * POSITION. The header drags the window anywhere on screen: up, down, left and
 * right, kept CHAT_EDGE_GAP inside the left, right and bottom edges and never
 * under the navbar.
 *
 * Both are remembered per browser (localStorage), like the chat bubble's corner,
 * and pulled back on screen when the viewport shrinks.
 */

export const CHAT_MIN_WIDTH = 360;
export const CHAT_DEFAULT_WIDTH = 418;
export const CHAT_MAX_WIDTH = 960;
export const CHAT_MIN_HEIGHT = 420;
export const CHAT_DEFAULT_HEIGHT = 650;
export const CHAT_MAX_HEIGHT = 900;
/** The window's resting distance from the viewport's right edge (the former `right-[54px]`). */
export const CHAT_DEFAULT_RIGHT = 54;
/** Kept from the left, right and bottom edges (the former `bottom-2`). */
export const CHAT_EDGE_GAP = 8;
export const CHAT_DEFAULT_BOTTOM = CHAT_EDGE_GAP;
/** The Layout navbar (`h-16`, see Header/Navbar.tsx): the window never goes under it. */
export const CHAT_TOP_CLEARANCE = 64;
const KEYBOARD_STEP_PX = 24;
const STORAGE_KEY = "alpha-chat-window";

/** Elements inside the header that keep their own pointer behaviour — a press on one never moves the window. */
const NO_DRAG_SELECTOR = 'button, a, input, textarea, select, [role="menu"], [data-chat-no-drag]';

export interface ChatWindowGeometry {
  width: number;
  height: number;
  right: number;
  bottom: number;
}

/** What a drag changes: one edge, the corner (both edges), or the window's position. */
type DragKind = "corner" | "left" | "move" | "top";

interface Drag extends ChatWindowGeometry {
  kind: DragKind;
  startX: number;
  startY: number;
}

const between = (value: number, low: number, high: number): number =>
  Math.round(Math.min(Math.max(low, high), Math.max(low, value)));

/** The widest the window may be with its right edge `right` from the viewport's (CHAT_MAX_WIDTH at most). */
export const maxChatWidth = (right = CHAT_EDGE_GAP): number =>
  Math.max(CHAT_MIN_WIDTH, Math.min(CHAT_MAX_WIDTH, window.innerWidth - CHAT_EDGE_GAP - right));

/** The tallest the window may be with its bottom edge `bottom` from the viewport's (CHAT_MAX_HEIGHT at most). */
export const maxChatHeight = (bottom = CHAT_EDGE_GAP): number =>
  Math.max(
    CHAT_MIN_HEIGHT,
    Math.min(CHAT_MAX_HEIGHT, window.innerHeight - CHAT_TOP_CLEARANCE - bottom)
  );

/** A geometry that fits on screen: the size first (it may not exceed the screen), then the position. */
export const clampChatGeometry = (geometry: ChatWindowGeometry): ChatWindowGeometry => {
  const width = between(geometry.width, CHAT_MIN_WIDTH, maxChatWidth());
  const height = between(geometry.height, CHAT_MIN_HEIGHT, maxChatHeight());
  return {
    width,
    height,
    right: between(geometry.right, CHAT_EDGE_GAP, window.innerWidth - width - CHAT_EDGE_GAP),
    bottom: between(
      geometry.bottom,
      CHAT_EDGE_GAP,
      window.innerHeight - CHAT_TOP_CLEARANCE - height
    ),
  };
};

const DEFAULTS: ChatWindowGeometry = {
  width: CHAT_DEFAULT_WIDTH,
  height: CHAT_DEFAULT_HEIGHT,
  right: CHAT_DEFAULT_RIGHT,
  bottom: CHAT_DEFAULT_BOTTOM,
};

const readSaved = (): ChatWindowGeometry => {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null") || {};
    const pick = (key: keyof ChatWindowGeometry) =>
      Number.isFinite(Number(saved[key])) && saved[key] !== null
        ? Number(saved[key])
        : DEFAULTS[key];
    return {
      width: pick("width"),
      height: pick("height"),
      right: pick("right"),
      bottom: pick("bottom"),
    };
  } catch {
    return DEFAULTS;
  }
};

const save = (geometry: ChatWindowGeometry): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(geometry));
  } catch {
    /* storage unavailable — the geometry lasts for this page only */
  }
};

/** The geometry a pointer moved by (dx, dy) gives the drag in progress. The bottom-right corner anchors a resize. */
const dragged = (drag: Drag, dx: number, dy: number): ChatWindowGeometry => {
  const { width, height, right, bottom } = drag;
  const widthFor = (w: number) => between(w, CHAT_MIN_WIDTH, maxChatWidth(right));
  const heightFor = (h: number) => between(h, CHAT_MIN_HEIGHT, maxChatHeight(bottom));
  switch (drag.kind) {
    case "top":
      return { width, height: heightFor(height - dy), right, bottom };
    case "left":
      return { width: widthFor(width - dx), height, right, bottom };
    case "corner":
      return { width: widthFor(width - dx), height: heightFor(height - dy), right, bottom };
    default:
      // The window follows the pointer, on screen.
      return clampChatGeometry({ width, height, right: right - dx, bottom: bottom - dy });
  }
};

export const useChatWindowGeometry = () => {
  const [geometry, setGeometry] = useState<ChatWindowGeometry>(() =>
    clampChatGeometry(readSaved())
  );
  const [activeDrag, setActiveDrag] = useState<DragKind | null>(null);
  const dragRef = useRef<Drag | null>(null);

  // Keep the window on screen when the viewport shrinks.
  const reclamp = useCallback(() => setGeometry((current) => clampChatGeometry(current)), []);

  useEffect(() => {
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [reclamp]);

  const begin =
    (kind: DragKind) =>
    (event: React.PointerEvent<HTMLElement>): void => {
      if (event.button !== 0) return;
      if (kind === "move" && (event.target as HTMLElement).closest?.(NO_DRAG_SELECTOR)) return;
      dragRef.current = { kind, startX: event.clientX, startY: event.clientY, ...geometry };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setActiveDrag(kind);
      event.preventDefault(); // no text selection while dragging
    };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>): void => {
    const drag = dragRef.current;
    if (drag) setGeometry(dragged(drag, event.clientX - drag.startX, event.clientY - drag.startY));
  };

  const finish = (event: React.PointerEvent<HTMLElement>, keep: boolean): void => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setActiveDrag(null);
    // A cancelled gesture (the browser aborted it) goes back to where it started.
    const started = {
      width: drag.width,
      height: drag.height,
      right: drag.right,
      bottom: drag.bottom,
    };
    const next = keep
      ? dragged(drag, event.clientX - drag.startX, event.clientY - drag.startY)
      : started;
    setGeometry(next);
    if (keep) save(next);
  };

  const pointerHandlers = (kind: DragKind) => ({
    onPointerDown: begin(kind),
    onPointerMove,
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => finish(event, true),
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => finish(event, false),
  });

  /** An edge from the keyboard: the arrow that grows it, the one that shrinks it, Home / End to the limits. */
  const keyResize =
    (axis: "height" | "width", grow: string, shrink: string) =>
    (event: React.KeyboardEvent<HTMLElement>): void => {
      const size = geometry[axis];
      const [low, high] =
        axis === "height"
          ? [CHAT_MIN_HEIGHT, maxChatHeight(geometry.bottom)]
          : [CHAT_MIN_WIDTH, maxChatWidth(geometry.right)];
      const target: Record<string, number> = {
        [grow]: size + KEYBOARD_STEP_PX,
        [shrink]: size - KEYBOARD_STEP_PX,
        Home: low,
        End: high,
      };
      if (!(event.key in target)) return;
      event.preventDefault();
      const next = { ...geometry, [axis]: between(target[event.key], low, high) };
      setGeometry(next);
      save(next);
    };

  return {
    ...geometry,
    maxWidth: maxChatWidth(geometry.right),
    maxHeight: maxChatHeight(geometry.bottom),
    activeDrag,
    // Bottom-right anchored: the top edge grows UP, the left edge grows LEFT.
    topEdgeHandlers: {
      ...pointerHandlers("top"),
      onKeyDown: keyResize("height", "ArrowUp", "ArrowDown"),
    },
    leftEdgeHandlers: {
      ...pointerHandlers("left"),
      onKeyDown: keyResize("width", "ArrowLeft", "ArrowRight"),
    },
    cornerHandlers: pointerHandlers("corner"),
    moveHandlers: pointerHandlers("move"),
  };
};
